import * as React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import Icon from 'common/components/Icon';
import { LatanaText } from 'common/components/LatanaText';
import { SelectPopupItemProps } from 'common/types/selectMenu';
import { useMemo } from 'react';
import ActionMenu from '../ActionMenu';

const FolderContainer = styled.div`
  display: flex;
  border-radius: ${({ theme }) => theme.v2.borderRadius}px;
  background: transparent;
  cursor: pointer;
  align-items: center;
  position: relative;
  width: 100%;
  padding: ${({ theme }) => theme.v2.space(0.5, 1)};
  padding-left: ${({ theme }) => theme.v2.space(1.5)};
  height: 100%;

  &:hover {
    background: ${({ theme }) => theme.v2.colours.gray100};
  }
`;

const FolderIcon = styled.div`
  margin-right: ${({ theme }) => theme.space(1)};
`;

const FolderName = styled.div`
  flex: 1;
  pointer-events: auto;
`;

const FolderCount = styled.div`
  color: ${({ theme }) => theme.v2.colours.gray500};
  margin-left: ${({ theme }) => theme.space(2)};
`;

const NameInput = styled.input`
  background: transparent;
  border: none;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: ${({ theme }) => theme.v2.text.variants.L1.fontSize};
  font-weight: ${({ theme }) => theme.v2.text.variants.L1.fontWeight};
  color: ${({ theme }) => theme.v2.colours.gray900};
  width: 100%;
  pointer-events: auto;

  &:focus {
    outline: none;
  }
`;

const CountBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.v2.colours.gray100};
  border-radius: 12px;
  padding: 2px 8px;
  min-width: 20px;
  height: 20px;
  margin-right: ${({ theme }) => theme.space(1)};
`;

const ActionMenuWrapper = styled.div`
  pointer-events: auto;
`;

interface FolderItemComponentProps {
  name: string;
  count: number;
  onDoubleClick: () => void;
  onRename?: (newName: string) => void;
  onDelete?: () => void;
  'data-testid'?: string;
}

const FolderItem: React.FC<FolderItemComponentProps> = ({ name, count, onDoubleClick, onRename, onDelete, 'data-testid': dataTestid }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = React.useState(false);
  const [folderName, setFolderName] = React.useState(name);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const folderRef = React.useRef<HTMLDivElement>(null);

  const folderActions = useMemo(() => {
    const actions: SelectPopupItemProps[] = [];
    if (onRename) actions.push({ label: t('actionMenu.rename'), value: 'rename', icon: 'pencil' });
    if (onRename && onDelete) actions.push({ label: '', value: 'separator' });
    if (onDelete)
      actions.push({
        label: { name: t('actionMenu.delete'), color: { active: 'red500', disabled: 'red200' } },
        value: 'delete',
        icon: { name: 'trash-can', color: { active: 'red400', disabled: 'red200' } },
      });
    return actions;
  }, [t, onRename, onDelete]);

  const handleActionSelect = (actions: string[]) => {
    const action = actions[0]; // Get the first action from the array

    if (action === 'rename' && onRename) {
      setIsEditing(true);
    } else if (action === 'delete' && onDelete) {
      onDelete();
    }
  };

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEditing) return;

    // Navigate to folder
    onDoubleClick();
  };

  const handleNameDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRename) setIsEditing(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    if (folderName.trim() !== '' && folderName !== name) {
      onRename ? onRename(folderName) : setFolderName(name);
    } else {
      setFolderName(name);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setFolderName(name);
    }
  };

  return (
    <FolderContainer onClick={handleDoubleClick} ref={folderRef} data-testid={dataTestid}>
      <FolderIcon>
        <Icon icon={'folder'} color='latana' size={16} />
      </FolderIcon>
      <FolderName onDoubleClick={handleNameDoubleClick}>
        {isEditing ? (
          <NameInput ref={inputRef} value={folderName} onChange={handleInputChange} onBlur={handleInputBlur} onKeyDown={handleKeyDown} />
        ) : (
          <LatanaText variant='L1' color='gray900'>
            {name}
          </LatanaText>
        )}
      </FolderName>
      <FolderCount>
        <CountBadge>
          <LatanaText variant='B2' color='gray500'>
            {count}
          </LatanaText>
        </CountBadge>
      </FolderCount>

      {folderActions.length > 0 && (
        <ActionMenuWrapper>
          <ActionMenu id={`folder-action-${name}`} icon='more' stopPropagation items={folderActions} onSelect={handleActionSelect} />
        </ActionMenuWrapper>
      )}
    </FolderContainer>
  );
};

export default FolderItem;
